import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import { toValue } from 'vue';

import { DATA_TYPE_TIME_UNITS } from '@/app/dashboards/constants';
import { METRIC_TYPE_REPORT_KEYS } from '@/app/dashboards/utils/reports.enum';
import {
  CHANNELS,
  isEarnedChannel as checkIsEarnedChannel,
  isUGCMetric as checkIsUGCMetric,
} from '@/models/dashboards/channels.enum';
import { GRAPH_STATS } from '@/models/dashboards/graph-stats.enum';
import { MEDIA_TYPES } from '@/models/dashboards/media-types.enum';
import {
  AGGREGATION_CURRENT_GRAPH_STATS,
  AGGREGATION_NO_GRAPH_STATS,
  AGGREGATION_ONLY_AVG_GRAPH_STATS,
  AGGREGATION_TYPES,
  DATA_TYPE_FORMATS,
  DATA_TYPES,
  POST_TYPES,
  QUERY_TIMEFRAME,
} from '@/models/dashboards/metrics.constants';
import { useDashboardReportsStore } from '@/stores/dashboards-reports';
import { useMetricsStore } from '@/stores/metrics';
import { formatIntegerWithRules, formatValueByFormatType } from '@/utils/formatters';
import { logger } from '@/utils/logger';

/**
 * Pure derivation of a metric's detail fields from the (fetch-once, static) metrics-store data.
 * Reads the store's lookups but creates NO reactive computeds, so it's safe to call anywhere —
 * util fns, `.map` callbacks, or wrapped in a single `computed` for reactive callers. This is the
 * shared core behind both `useMetricDetails` and `getMetricDetails`.
 *
 * Covers everything except the flag-dependent fields (breakdownOptions, secondaryBreakdownOptions,
 * restrictedVisualizationOptions) and the all-channels custom-metric helper, which need `flagStore`
 * + extra inputs and stay in `useMetricDetails`.
 */
export function deriveMetricDetails(options = {}) {
  const metricsStore = useMetricsStore();
  const dashboardReportsStore = useDashboardReportsStore();

  // Inputs may be refs/getters (reactive callers) or plain values (util callers).
  const metric = toValue(options.metric);
  const channels = toValue(options.channels);
  const metricReportTypeKey = toValue(options.metricReportTypeKey);

  const channel = channels?.[0];
  const isMultiChannel = channels?.length > 1;
  const isContentReport = metricReportTypeKey === METRIC_TYPE_REPORT_KEYS.CONTENT_REPORT;
  const isGraphReport = metricReportTypeKey === METRIC_TYPE_REPORT_KEYS.GRAPH_REPORT;

  // Single-channel details are needed both for single-channel reports and for the multi-channel
  // `isNonAdditive` read (which uses the first channel's value).
  const firstChannelDetails = metricsStore.getTargetMetricDetails(
    channel,
    metricReportTypeKey,
    metric,
  );
  const target = isMultiChannel
    ? metricsStore.getTargetMetricDetailsMultiChannel(metric, isContentReport)
    : firstChannelDetails;

  const dataType = target?.data_type;
  const formats = DATA_TYPE_FORMATS?.[dataType];
  const isIntegerMetric = dataType === DATA_TYPES.INTEGER;
  const isFloatMetric = dataType === DATA_TYPES.FLOAT;
  const supportsMediaBreakdown = target?.supports_media_breakdown;

  let aggregationType = target?.aggregation;
  if (aggregationType === AGGREGATION_TYPES.CUSTOM) {
    aggregationType = target?.aggregation_type;
  }

  function convertMetricToRequiredUnit(value) {
    // seconds is the preferred unit to be displayed
    if (dataType === DATA_TYPE_TIME_UNITS.MILLISECONDS) {
      return (value / 1000).toFixed(2);
    }
    return value;
  }

  function formatMetricValue(
    value,
    formatType,
    unit = null,
    isSmallTile = false,
    fromDashboards = false,
  ) {
    const emptyValue = '-';
    if (isNil(value)) return emptyValue;

    const format = formats?.[formatType];
    const convertedValue = convertMetricToRequiredUnit(value);

    let formattedValue;
    if (isNumber(convertedValue) && format && isIntegerMetric && isFloatMetric && fromDashboards) {
      formattedValue = formatIntegerWithRules(convertedValue, isSmallTile);
    } else if (format) {
      formattedValue = formatValueByFormatType(convertedValue, format, emptyValue);
    } else {
      logger.error(`formatMetricValue: missing format for '${formatType}'`);
      return emptyValue;
    }
    if (unit) formattedValue = `${formattedValue} ${unit}`;
    return formattedValue;
  }

  function graphStats() {
    if (!isGraphReport) return null;
    if (
      AGGREGATION_NO_GRAPH_STATS.includes(aggregationType) ||
      (AGGREGATION_ONLY_AVG_GRAPH_STATS.includes(aggregationType) && isMultiChannel)
    ) {
      return [];
    }
    if (AGGREGATION_ONLY_AVG_GRAPH_STATS.includes(aggregationType)) {
      return [GRAPH_STATS.MEAN_BY_DAY.value];
    }
    if (target?.hide_total) {
      return [GRAPH_STATS.MEAN_BY_DAY.value];
    }
    if (AGGREGATION_CURRENT_GRAPH_STATS.includes(aggregationType)) {
      return [GRAPH_STATS.CURRENT.value, GRAPH_STATS.MEAN_BY_DAY.value];
    }
    return [GRAPH_STATS.SUM.value, GRAPH_STATS.MEAN_BY_DAY.value];
  }

  function mediaTypeLabel() {
    if (!supportsMediaBreakdown) return null;
    if (!target?.media_type_label) return MEDIA_TYPES.ALL_MEDIA.text;
    return target?.media_type_label;
  }

  // These three hit dashboardReportsStore (comparable metrics). Exposed as lazy getters below so a
  // caller reading only pure fields never triggers those lookups — matching the old lazy computeds.
  function computeBaseMetricDetails() {
    // Per-channel comparable-metric details, used to union post types for multi-channel metrics.
    return (channels ?? []).reduce((acc, ch) => {
      const comparableMetric = dashboardReportsStore.getComparableMetricBasedOnChannel(
        ch,
        metric,
        isContentReport,
      );
      if (comparableMetric) {
        acc[ch] = metricsStore.getTargetMetricDetails(ch, metricReportTypeKey, comparableMetric);
      }
      return acc;
    }, {});
  }

  function resolveRepresentativePostType(postTypes) {
    if (postTypes.length < 1) return null;
    if (postTypes.length === 1) return postTypes[0];
    const hasOrganic = postTypes.includes(POST_TYPES.ORGANIC);
    const hasPromoted = postTypes.includes(POST_TYPES.PROMOTED);
    if (hasOrganic && hasPromoted) return POST_TYPES.ORGANIC_PROMOTED;
    return postTypes[0];
  }

  function computePostType() {
    const ownPostTypes = target?.post_types ?? [];
    if (!isMultiChannel || ownPostTypes.length > 0) {
      return resolveRepresentativePostType(ownPostTypes);
    }
    const unioned = new Set();
    Object.values(computeBaseMetricDetails()).forEach((details) => {
      (details?.post_types ?? []).forEach((type) => unioned.add(type));
    });
    const unionedPostTypes = [...unioned];
    if (
      unionedPostTypes.length > 0 &&
      unionedPostTypes.every((t) => [POST_TYPES.PROMOTED, POST_TYPES.ADS].includes(t))
    ) {
      return POST_TYPES.PROMOTED;
    }
    return resolveRepresentativePostType(unionedPostTypes);
  }

  function computeRequiredFeatureFlag() {
    if (isMultiChannel && channels.includes(CHANNELS.YOUTUBE.value)) {
      const ytKey = dashboardReportsStore.comparableMetrics?.[metric]?.[CHANNELS.YOUTUBE.value];
      const ytDetails = metricsStore.getTargetMetricDetails(
        CHANNELS.YOUTUBE.value,
        metricReportTypeKey,
        ytKey,
      );
      return ytDetails?.required_feature_flag;
    }
    return target?.required_feature_flag;
  }

  return {
    get baseMetricDetails() {
      return computeBaseMetricDetails();
    },
    get postType() {
      return computePostType();
    },
    get requiredFeatureFlag() {
      return computeRequiredFeatureFlag();
    },
    targetMetricDetails: target,
    aggregationType,
    allowContentTags: target?.allow_content_tags,
    supportsCollab: target?.supports_collab ?? false,
    allowGoals: target?.allow_goals,
    connectedAccountsOnly: target?.connected_accounts_only,
    dataSource: target?.data_source,
    deprecated: target?.deprecated,
    disabledDisplayStrategies: target?.disabled_display_strategies ?? [],
    displayName: target?.display_name,
    dataType,
    formula: target?.formula,
    graphBeginAtZero: isGraphReport
      ? aggregationType !== AGGREGATION_TYPES.LATEST &&
        aggregationType !== AGGREGATION_TYPES.LATEST_VALID
      : null,
    graphPrecision: isGraphReport ? (dataType === DATA_TYPES.PERCENTAGE ? 4 : 0) : null,
    graphStats: graphStats(),
    hideOnDashboards: target?.hide_on_dashboards,
    isEarnedChannel: checkIsEarnedChannel(channel),
    isGalleryMetric: target?.is_gallery_metric ?? false,
    isIntegerMetric,
    isNegativeMetric: target?.is_negative_metric,
    isMonetaryMetric: dataType === DATA_TYPES.MONETARY,
    isNonAdditive: isMultiChannel
      ? (firstChannelDetails?.is_non_additive ?? false)
      : (target?.is_non_additive ?? false),
    isUGCMetric: checkIsUGCMetric(metric),
    lockedMediaBreakdown: target?.locked_media_breakdown,
    mediaTypeLabel: mediaTypeLabel(),
    metricCategories: target?.metric_categories,
    parentMetric: target?.parent_metric ?? metric,
    permissions: target?.permissions,
    sourceDataName: isContentReport ? target?.source_data_name : null,
    supportsMediaBreakdown,
    timeframe: isContentReport ? QUERY_TIMEFRAME.POSTS_PUBLISHED_IN_TIMEFRAME : target?.timeframe,
    timeScaleLock: target?.time_scale_lock,
    tooltip: target?.description,
    formatMetricValue,
  };
}
